import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import LinearGradient from 'react-native-linear-gradient';
import { Bulletin } from '@/types/residents/bulletin';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import BulletinImagesCarousel from '@/components/common/bulletins/BulletinImagesCarousel';
import BulletinDescription from '@/components/common/bulletins/BulletinDescription';
import { sanitizeResidentFiles } from '@/utils/shared/bulletins/sanitizeResidentFiles';
import { getBulletinDescription, formatResidentBulletinDate } from '@/utils/residents/bulletins/format';

interface Props {
  bulletin: Bulletin;
  isExpanded?: boolean;
  onToggleExpanded?: (id: number) => void;
}

export const ResidentBulletinCard: React.FC<Props> = ({ bulletin, isExpanded = false, onToggleExpanded }) => {
  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const files = sanitizeResidentFiles(bulletin.files);
  const description = getBulletinDescription(bulletin);
  const dateText = formatResidentBulletinDate(bulletin.publishDate || bulletin.createdAt);

  const minImageHeight = 200;
  const maxImageHeight = 360;
  const computeHeight = (w: number) => Math.max(minImageHeight, Math.min(maxImageHeight, Math.round(w * 9/16)));

  return (
    <View style={styles.cardContainer}>
      {bulletin.highlight && (
        <LinearGradient
          colors={["#4773FE", "#A855F7", "#EF4444"]}
          locations={[0.1138, 0.6385, 0.9773]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.gradientBorder}
        />
      )}
      <View style={[styles.contentContainer, bulletin.highlight && styles.contentContainerWithBorder] }>
        <View style={styles.headerTop}>
          {!!dateText && <Text style={styles.dateText}>{dateText}</Text>}
          <View style={styles.badgesContainer}>
            {bulletin.pinHomepage && (
              <View style={styles.pinBadge}>
                <SVG_COMPONENTS.PROPERTY width={12} height={12} color={semantic.text.primary} />
              </View>
            )}
          </View>
        </View>

        <Text style={styles.title}>{bulletin.title || 'Untitled'}</Text>

        {files.length > 0 && (
          <BulletinImagesCarousel files={files} computeHeight={computeHeight} maxImageHeight={maxImageHeight} />
        )}

        {!!description && (
          <BulletinDescription text={description} expanded={isExpanded} onToggle={() => onToggleExpanded && onToggleExpanded(bulletin.id)} />
        )}
      </View>
    </View>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  cardContainer: { position: 'relative', borderRadius: 8 },
  gradientBorder: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 8 },
  contentContainer: { backgroundColor: semantic.background.secondary, borderRadius: 8, padding: 16 },
  contentContainerWithBorder: { borderRadius: 6, margin: 2 },
  headerTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  dateText: { fontSize: 12, color: semantic.text.tertiary, fontWeight: '400' },
  badgesContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pinBadge: { backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 12, padding: 6 },
  title: { fontSize: 20, fontWeight: '600', color: semantic.text.primary, lineHeight: 28, marginBottom: 8 },
});

export default ResidentBulletinCard;


